import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Warning, ArrowRight } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { MemberDocumentLink } from '../components/MemberDocuments';
import InstallGuide from '../components/InstallGuide';
import parchment from '../assets/parchment.webp';
import cardSeal from '../assets/card-seal.png';
import invitationPlate from '../assets/invitation-plate.webp';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/* The letter carries its own fixed palette. A letter is a letter — it reads the
   same in light and dark mode, like paper does. */
const INK = '#2E1B12';
const INK_SOFT = '#6B5442';
const INK_FAINT = '#8A7256';
const GOLD_INK = '#8C6B2F';
const WAX = '#4A1622';

const SEAL_BREAK_MS = 800;

const wax = {
  borderRadius: '9999px',
  background: 'radial-gradient(circle at 34% 28%, #8A2531 0%, #5C1A24 46%, #3A0F18 100%)',
  border: '1px solid rgba(201,169,106,0.55)',
  boxShadow:
    'inset 0 3px 10px rgba(255,255,255,0.10), inset 0 -8px 16px rgba(0,0,0,0.45), 0 10px 26px rgba(0,0,0,0.5)',
};

/** One half of the wax seal, clipped along a slightly angled crack. */
const SealHalf = ({ side, breaking }) => (
  <span
    aria-hidden="true"
    className={`absolute inset-0 ${breaking ? (side === 'left' ? 'seal-half-left' : 'seal-half-right') : ''}`}
    style={{
      ...wax,
      clipPath:
        side === 'left'
          ? 'polygon(0 0, 53% 0, 47% 100%, 0 100%)'
          : 'polygon(53% 0, 100% 0, 100% 100%, 47% 100%)',
    }}
  >
    <img
      src={cardSeal}
      alt=""
      className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[62%]"
      style={{ opacity: 0.9 }}
    />
  </span>
);

const Onboarding = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState('verifying'); // verifying | ready | invalid | accepting | success
  const [userInfo, setUserInfo] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [autoLoginError, setAutoLoginError] = useState('');
  const [sealBroken, setSealBroken] = useState(false);
  const [breaking, setBreaking] = useState(false);
  const breakTimer = useRef(null);

  useEffect(() => {
    const verify = async () => {
      try {
        const { data } = await axios.post(`${API_URL}/api/onboarding/verify`, { token });
        setUserInfo(data);
        setStatus('ready');
      } catch (e) {
        setError(e.response?.data?.detail || 'This invitation is no longer valid.');
        setStatus('invalid');
      }
    };
    verify();
  }, [token]);

  useEffect(() => () => clearTimeout(breakTimer.current), []);

  const breakSeal = () => {
    if (breaking || sealBroken) return;
    // Anyone who asked for less motion gets the letter straight away.
    const still = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (still) {
      setSealBroken(true);
      return;
    }
    setBreaking(true);
    breakTimer.current = setTimeout(() => setSealBroken(true), SEAL_BREAK_MS - 120);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 8) {
      setError('A key must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('The two keys do not match.');
      return;
    }

    setStatus('accepting');
    try {
      await axios.post(`${API_URL}/api/onboarding/accept`, { token, password });
      // Sign them in immediately so admission flows straight into the hall,
      // never through /login.
      const result = await login(userInfo.email, password);
      if (!result.success) {
        setAutoLoginError(result.error || 'Please continue to sign in.');
      }
      setStatus('success');
    } catch (e) {
      setError(e.response?.data?.detail || 'Admission could not be completed.');
      setStatus('ready');
    }
  };

  const name = userInfo?.display_name || userInfo?.name;

  // ---- admission recorded ----
  if (status === 'success') {
    return (
      <div className="invitation-palette min-h-screen bg-background flex items-start justify-center p-4 pt-12">
        <div className="w-full max-w-md space-y-6" data-testid="onboarding-success">
          <div className="text-center">
            <img src="/icon-192.png" alt="" className="inline-block w-16 h-16 rounded-2xl mb-4" />
            <h1 className="text-2xl font-heading tracking-tight text-foreground">
              Your admission is recorded
            </h1>
            <p className="text-sm text-muted-foreground mt-2">
              The Society is open to you, {name}.
            </p>
          </div>

          <InstallGuide />

          {autoLoginError && (
            <p className="text-xs text-muted-foreground text-center" data-testid="onboarding-autologin-warning">
              {autoLoginError}
            </p>
          )}

          <Button
            onClick={() => navigate('/')}
            className="w-full h-11 bg-primary hover:bg-primary/90"
            data-testid="onboarding-continue-button"
          >
            Enter Arcanum
            <ArrowRight className="w-4 h-4 ml-2" weight="bold" />
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="invitation-palette min-h-screen bg-background flex items-center justify-center p-4 py-10">
      <div className="w-full max-w-md" data-testid="onboarding-page">
        {status === 'verifying' && (
          <div className="text-center py-10">
            <div className="w-7 h-7 border-2 border-secondary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-sm text-muted-foreground">Examining the seal…</p>
          </div>
        )}

        {status === 'invalid' && (
          <div className="text-center py-10" data-testid="onboarding-invalid">
            <Warning className="w-10 h-10 text-destructive mx-auto mb-4" weight="fill" />
            <h2 className="font-heading text-xl text-foreground mb-2">The seal is broken</h2>
            <p className="text-sm text-muted-foreground mb-6">{error}</p>
            <Button onClick={() => navigate('/login')} variant="outline" data-testid="back-to-login">
              Return to the gate
            </Button>
          </div>
        )}

        {/* ---- sealed: the invitation waits ---- */}
        {status === 'ready' && userInfo && !sealBroken && (
          <div className="text-center" data-testid="wax-seal">
            <p className="text-[11px] uppercase tracking-[0.28em] mb-2" style={{ color: 'hsl(var(--gold))' }}>
              Arcanum
            </p>
            <p className="font-heading text-xl text-foreground mb-1">
              An invitation stands in your name
            </p>
            <p className="text-sm text-muted-foreground mb-9">{name}</p>

            <button
              type="button"
              onClick={breakSeal}
              className="group inline-flex flex-col items-center focus:outline-none"
              aria-label="Break the seal to read the invitation"
            >
              <span className="relative inline-block w-28 h-28 mb-5 transition-transform duration-300 group-hover:scale-[1.03] group-active:scale-95">
                <SealHalf side="left" breaking={breaking} />
                <SealHalf side="right" breaking={breaking} />
              </span>
              <span
                className="text-xs uppercase tracking-[0.22em] transition-opacity duration-300"
                style={{ color: 'hsl(var(--gold))', opacity: breaking ? 0 : 1 }}
              >
                Break the seal
              </span>
            </button>
          </div>
        )}

        {/* ---- the letter ---- */}
        {(status === 'ready' || status === 'accepting') && userInfo && sealBroken && (
          <div
            className="letter-unfold rounded-sm overflow-hidden"
            style={{
              backgroundImage: `url(${parchment})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              boxShadow: '0 18px 50px rgba(0,0,0,0.55)',
              color: INK,
            }}
          >
            <div className="px-7 py-8 sm:px-9">
              {/* The illuminated plate. An illuminated manuscript is a
                  decorated letter, so the painting belongs at the head of one
                  — and its ochre and gold sit far better on parchment than
                  they would on any dark screen. */}
              <img
                src={invitationPlate}
                alt="The Society's gate, painted in watercolour"
                className="w-full mb-7"
                style={{ boxShadow: '0 6px 18px rgba(60,44,30,0.35)' }}
              />

              {/* masthead */}
              <div className="text-center mb-7">
                <p className="font-heading text-2xl tracking-[0.2em]" style={{ color: INK }}>
                  ARCANUM
                </p>
                <p className="text-[9px] uppercase tracking-[0.3em] mt-1" style={{ color: GOLD_INK }}>
                  The Exclusive Society
                </p>
                <div
                  className="mx-auto mt-4"
                  style={{
                    height: 1,
                    width: '4.5rem',
                    background: `linear-gradient(90deg, transparent, ${GOLD_INK}, transparent)`,
                  }}
                />
              </div>

              {/* the body of the letter */}
              <p className="text-[15px] leading-relaxed mb-1" style={{ color: INK }}>
                <span className="font-heading text-lg" data-testid="onboarding-display-name">{name}</span>
                {' '}has been proposed for admission to the Society.
              </p>
              {userInfo.proposed_by && (
                <p className="text-sm mb-1" style={{ color: INK_SOFT }}>
                  Proposed by {userInfo.proposed_by}.
                </p>
              )}
              <p className="text-xs font-mono mb-7" style={{ color: INK_FAINT }}>
                {userInfo.email}
              </p>

              {/* terms, not features — an institution setting expectations */}
              <div
                className="mb-7 py-4 text-[13px] leading-relaxed"
                style={{ borderTop: `1px solid ${GOLD_INK}44`, borderBottom: `1px solid ${GOLD_INK}44`, color: INK_SOFT }}
                data-testid="onboarding-security-note"
              >
                <p className="text-[9px] uppercase tracking-[0.24em] mb-2" style={{ color: GOLD_INK }}>
                  Terms of membership
                </p>
                <p>
                  Membership is personal and non-transferable. Discretion is expected of
                  every member. Conversations are held on the Society's own servers and
                  are not end-to-end encrypted — the Society can read what passes
                  through it.
                </p>
                {/* The letter stays a letter. The detail is a separate paper,
                    offered rather than pressed, and it opens in the app's own
                    dark so it reads as a different document. */}
                <MemberDocumentLink
                  id="before-you-accept"
                  className="mt-3 underline underline-offset-4 hover:no-underline"
                  style={{ color: GOLD_INK }}
                >
                  Read what this means, in full
                </MemberDocumentLink>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="onboarding-form">
                <div className="space-y-1.5">
                  <label
                    htmlFor="password"
                    className="block text-[9px] uppercase tracking-[0.24em]"
                    style={{ color: GOLD_INK }}
                  >
                    Choose your key
                  </label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="At least 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    className="h-11"
                    style={{
                      background: 'rgba(255,255,255,0.55)',
                      borderColor: `${GOLD_INK}55`,
                      color: INK,
                    }}
                    data-testid="onboarding-password"
                  />
                </div>

                <div className="space-y-1.5">
                  <label
                    htmlFor="confirm"
                    className="block text-[9px] uppercase tracking-[0.24em]"
                    style={{ color: GOLD_INK }}
                  >
                    Confirm your key
                  </label>
                  <Input
                    id="confirm"
                    type="password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    required
                    className="h-11"
                    style={{
                      background: 'rgba(255,255,255,0.55)',
                      borderColor: `${GOLD_INK}55`,
                      color: INK,
                    }}
                    data-testid="onboarding-confirm"
                  />
                </div>

                {error && (
                  <p
                    className="text-sm"
                    style={{ color: '#8B1E1E' }}
                    data-testid="onboarding-error"
                  >
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'accepting'}
                  className="w-full h-11 rounded-sm text-sm uppercase tracking-[0.18em] transition-opacity hover:opacity-90 disabled:opacity-60"
                  style={{ background: WAX, color: '#E8D9A8', border: `1px solid ${GOLD_INK}` }}
                  data-testid="onboarding-submit"
                >
                  {status === 'accepting' ? 'Recording…' : 'Accept admission'}
                </button>
              </form>

              <p className="text-[11px] text-center mt-6" style={{ color: INK_FAINT }}>
                This seal may be broken once, and will not hold beyond 48 hours.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Onboarding;
